// Several files chosen together wait here before upload so the user can put
// them in order: the first names the resume, and originals attached to the
// output follow the report in this order. Native drag and drop, no library.

import { useState } from "react";
import { GripVertical, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { cn } from "@/lib/utils";

function move<T>(list: T[], from: number, to: number): T[] {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export function UploadStage({
  files,
  onChange,
  onConfirm,
  onCancel,
  uploading,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  onConfirm: () => void;
  onCancel: () => void;
  uploading: boolean;
}) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const endDrag = () => {
    setDragIndex(null);
    setOverIndex(null);
  };
  const dropOn = (to: number) => {
    if (dragIndex != null && dragIndex !== to) onChange(move(files, dragIndex, to));
    endDrag();
  };

  return (
    <Card>
      <CardBody className="flex flex-col gap-3">
        <div>
          <p className="font-semibold text-text">Combine {files.length} files into one resume</p>
          <p className="text-[13px] text-text-2">
            Drag to set the order. The first file names the resume; originals attached to the
            output follow the report in this order.
          </p>
        </div>
        <ol className="flex flex-col gap-1.5">
          {files.map((file, i) => (
            <li
              key={`${file.name}-${file.size}-${i}`}
              draggable={!uploading}
              onDragStart={(e) => {
                setDragIndex(i);
                e.dataTransfer.effectAllowed = "move";
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (overIndex !== i) setOverIndex(i);
              }}
              onDragLeave={() => setOverIndex((o) => (o === i ? null : o))}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                dropOn(i);
              }}
              onDragEnd={endDrag}
              className={cn(
                "flex items-center gap-2 rounded-md border border-border bg-surface px-2.5 py-2 text-[13px]",
                dragIndex === i && "opacity-50",
                overIndex === i && dragIndex !== i && "border-brand-500 bg-brand-tint",
              )}
            >
              <GripVertical size={15} className="shrink-0 cursor-grab text-text-2" aria-hidden />
              <span className="w-5 shrink-0 text-center tabular-nums text-text-2">{i + 1}</span>
              <span className="min-w-0 flex-1 truncate text-text">{file.name}</span>
              <span className="shrink-0 text-[12px] text-text-2">{formatSize(file.size)}</span>
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                disabled={uploading}
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                className="shrink-0 rounded p-1 text-text-2 hover:text-text disabled:opacity-40"
              >
                <X size={14} aria-hidden />
              </button>
            </li>
          ))}
        </ol>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={onConfirm} disabled={uploading || files.length === 0}>
            <Upload size={15} strokeWidth={1.75} aria-hidden />
            {uploading
              ? "Uploading…"
              : `Upload ${files.length} file${files.length === 1 ? "" : "s"}`}
          </Button>
          <Button variant="secondary" onClick={onCancel} disabled={uploading}>
            Cancel
          </Button>
          <span className="text-[12px] text-text-2">
            Choose or drop more files to add them to this list.
          </span>
        </div>
      </CardBody>
    </Card>
  );
}
